/* @flow */

import classNames from 'classnames';
import { ErrorMessage } from 'formik';
import { compose } from 'ramda';
import * as React from 'react';
import { createUseStyles } from 'react-jss';

import styles from './FormGroup.style';

import FormikDatalist from './FormikDatalist';
import FormikInput from './FormikInput';
import FormikTextArea from './FormikTextArea';
import LengthCounter from 'src/shared/app/base/component/data-entry/length-counter/LengthCounter';
import useEventAccentColor from 'src/shared/domain/event/hook/useEventAccentColor';

type Props = {
  isTouchDevice: boolean,
  inputProps: Object,
};

const useStyles = createUseStyles(styles);

const FormikFormGroupDumb = ({
  isTouchDevice,
  inputProps,
}: Props): React.Node => {
  const { id, componentOpts, field, form, maxLength } = inputProps;
  const { name, value } = field;
  const {
    useTextarea,
    isHighlighted,
    hasLengthCounter,
    onLightBg,
    hideError,
    label,
    helper,
    list,
    inputComp,
    inputOpts,
  } = componentOpts || {};
  const isLengthCounterShowm = !!(hasLengthCounter && maxLength);
  const lengthCounterCharPadding = 40;
  const error = form.errors[name];
  const touched = form.touched[name];
  const safeProps = { ...inputProps };

  delete safeProps.componentOpts;
  delete safeProps.field;
  delete safeProps.form;

  safeProps.autoFocus = !isTouchDevice && safeProps.autoFocus;

  if (isLengthCounterShowm) {
    safeProps.maxLength = maxLength + lengthCounterCharPadding;
  }

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const [accentColor] = useEventAccentColor();

  /*
   |----------------------------------------------------------------------------
   | State
   |----------------------------------------------------------------------------
   */

  const [isFocused, setFocused] = React.useState(false);

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onFocus = () => setFocused(true);

  const onBlur = () => setFocused(false);

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles({ accentColor });

  const inputClasses = classNames(classes.input, {
    [`${classes['input-isInvalid']}`]: error && touched,
    [`${classes['input-onLightBg']}`]: onLightBg,
  });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const labelElt = label && (
    <label className={classes.label} htmlFor={id}>
      {label}
    </label>
  );

  const errorElt = !hideError && (
    <ErrorMessage name={name}>
      {(e) => (
        <span className={classes.error} aria-live="assertive">
          {e}
        </span>
      )}
    </ErrorMessage>
  );

  const helperElt = helper && !error && (
    <span className={classes.helper}>{helper}</span>
  );

  let inputElt;

  if (useTextarea) {
    inputElt = (
      <FormikTextArea
        className={inputClasses}
        field={field}
        inputProps={safeProps}
        isHighlighted={isHighlighted}
        hasLengthCounter={hasLengthCounter}
        handleFocus={onFocus}
        handleBlur={onBlur}
      ></FormikTextArea>
    );
  } else if (list) {
    inputElt = (
      <FormikDatalist
        className={inputClasses}
        field={field}
        inputProps={safeProps}
        list={list}
        handleFocus={onFocus}
        handleBlur={onBlur}
      ></FormikDatalist>
    );
  } else if (inputComp) {
    let props = {
      field,
      form,
      ...safeProps,
    };

    if (inputOpts) {
      props = {
        ...props,
        ...inputOpts,
      };
    }

    inputElt = React.createElement(inputComp, props);
  } else {
    inputElt = (
      <FormikInput
        className={inputClasses}
        field={field}
        inputProps={safeProps}
        handleFocus={onFocus}
        handleBlur={onBlur}
      ></FormikInput>
    );
  }

  let maxLengthElt;

  if (isLengthCounterShowm) {
    const { length } = value;
    const isOverLimit = length > Number(maxLength);

    if (isFocused || isOverLimit) {
      maxLengthElt = (
        <span className={classes.maxLengthCtn}>
          <LengthCounter count={length} maxLength={parseInt(maxLength, 10)} />
        </span>
      );
    }
  }

  return (
    <p className={classes.root}>
      {labelElt}
      {inputElt}
      {errorElt}
      {maxLengthElt}
      {helperElt}
    </p>
  );
};

export default compose(React.memo)(FormikFormGroupDumb);
